import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface Expert {
  id: string;
  full_name: string;
  title: string;
  specialization: string;
  experience_years: number;
  hourly_rate: number;
  rating: number;
  bio: string;
  availability: Record<string, string[]>;
  online_status?: 'online' | 'offline' | 'busy';
  reviews?: Array<{
    id: string;
    rating: number;
    comment: string;
    created_at: string;
    user: {
      id: string;
      email: string;
      full_name: string;
    };
  }>;
}

interface ExpertState {
  experts: Expert[];
  selectedExpert: Expert | null;
  loading: boolean;
  error: string | null;
  loadExperts: () => Promise<void>;
  selectExpert: (expert: Expert) => void;
  updateExpertStatus: (expertId: string, status: 'online' | 'offline' | 'busy') => void;
}

const useExpertStore = create<ExpertState>((set, get) => ({
  experts: [],
  selectedExpert: null,
  loading: false,
  error: null,

  loadExperts: async () => {
    set({ loading: true, error: null });
    try {
      // First fetch experts
      const { data: expertsData, error: expertsError } = await supabase
        .from('experts')
        .select('*')
        .order('rating', { ascending: false });

      if (expertsError) throw expertsError;

      // Then fetch reviews and user profiles for each expert
      const expertsWithReviews = await Promise.all(
        expertsData.map(async (expert) => {
          const { data: reviewsData, error: reviewsError } = await supabase
            .from('expert_reviews')
            .select(`
              id,
              rating,
              comment,
              created_at,
              user_id
            `)
            .eq('expert_id', expert.id);

          if (reviewsError) throw reviewsError;

          // For each review, fetch the user profile
          const reviewsWithUsers = await Promise.all(
            reviewsData.map(async (review) => {
              try {
                const { data: userData } = await supabase
                  .from('user_profiles')
                  .select('user_id, full_name')
                  .eq('user_id', review.user_id)
                  .maybeSingle();

                return {
                  ...review,
                  user: {
                    id: review.user_id,
                    full_name: userData?.full_name || 'Anonymous User'
                  }
                };
              } catch (error) {
                // If there's any error fetching the user profile, return the review with a default user
                return {
                  ...review,
                  user: {
                    id: review.user_id,
                    full_name: 'Anonymous User'
                  }
                };
              }
            })
          );

          return {
            ...expert,
            reviews: reviewsWithUsers
          };
        })
      );

      set({ experts: expertsWithReviews, loading: false });
    } catch (error) {
      console.error('Error loading experts:', error);
      set({ error: error.message, loading: false });
    }
  },

  selectExpert: (expert: Expert) => {
    set({ selectedExpert: expert });
  },

  updateExpertStatus: (expertId: string, status: 'online' | 'offline' | 'busy') => {
    set(state => ({
      experts: state.experts.map(expert =>
        expert.id === expertId
          ? { ...expert, online_status: status }
          : expert
      ),
      selectedExpert: state.selectedExpert?.id === expertId
        ? { ...state.selectedExpert, online_status: status }
        : state.selectedExpert
    }));
  }
}));

export default useExpertStore;